// Lib
import { cloneDeep, isEqual } from 'lodash';
import { convertFromRaw } from 'draft-js';
import Handsontable from 'handsontable/base';

// Utils
import { propIn } from '../../../../common/utils/immutableHelper';
import { createTableCellEntry, parseCellContentString } from './tableInitialisationUtils';
import { cellCanBeDraftFormatted, formatCellAsType, typeIsReadOnly } from './tableCellTypeUtils';
import rawHasText from '../../../../common/utils/editor/rawUtils/rawHasText';
import { isFormula } from './tableFormulaUtils';
import { getColumnCount, getRowCount } from './tableDataUtils';
import { getCellLevelTextStyles } from './tableCellFormattingUtils';

// Types
import { CellData, CellDataChange } from '../../../../common/table/TableTypes';

// Constants
import { TABLE_CELL_EDITOR_KEY, TABLE_GRID_EDITOR_KEY } from '../../../../common/table/tableConstants';

export const getTableCellEditorId = (elementId: string): string => `${elementId}-${TABLE_CELL_EDITOR_KEY}`;
export const getTableGridEditorId = (elementId: string): string => `${elementId}-${TABLE_GRID_EDITOR_KEY}`;

/**
 * Deduce a new cellData based on previous cell data and new value coming in
 */
export const createNewCellData = (prevCellData: CellData, newValue: string | null): CellData => {
    let newCellData = cloneDeep(prevCellData) || createTableCellEntry();
    newCellData.value = newValue;

    const content = newCellData.value && parseCellContentString(newCellData.value);
    const hasContent = typeof content === 'string' ? content !== '' : rawHasText(content);

    // If no content, always save as null for consistency
    if (!hasContent) {
        newCellData.value = null;

        if (typeIsReadOnly(newCellData.type)) {
            delete newCellData.type;
        }

        return newCellData;
    }

    // Check if we should set any cell type options by the cell contents,
    // e.g. change input of '$250,000' to a currency cell with value 25000
    newCellData = formatCellAsType(newCellData, prevCellData.value);

    // For cell content is not in draftJS format or cell is readonly cell, don't apply any formatting from inline styles
    if (typeof content !== 'object' || typeIsReadOnly(newCellData.type)) return newCellData;

    // if the inline styles don't match the cell level style, or the cell can't be draft formatted,
    // then update the cell level style to match the styles applied to the entire content
    const cellLevelTextStyles: string[] = content ? getCellLevelTextStyles(convertFromRaw(content)) : [];

    const stylesMatch = isEqual(cellLevelTextStyles.sort(), newCellData.textStyle?.sort() || []);
    if (!stylesMatch || !cellCanBeDraftFormatted(newCellData)) {
        newCellData.textStyle = cellLevelTextStyles;
    }

    return newCellData;
};

/**
 * Converts all cell changes [row, col, prevValue, newValue] to cellData changes [row, col, prevCellData, newCellData]
 */
export const createCellDataChanges = (
    changes: Array<Handsontable.CellChange>,
    data: Array<Array<CellData>>,
): Array<CellDataChange> => {
    return changes.map((change, index) => {
        const [row, col, , newValue] = change;

        const prevCellData = propIn([row, col], data);
        const newCellData = createNewCellData(prevCellData, newValue);

        return [row, col, prevCellData, newCellData];
    });
};

/**
 * Converts cell changes [row, col, prevValue, newValue] to cellDataChanges [row, col, prevCellData, newCellData],
 * specifically for changes that are copied from a source, such as copy/paste or autofill.
 *
 * For these type of changes, we'd need to find the origin of the copied data and apply the changes to the origin
 * cell data.
 */
export const createCellDataChangesFromSource = (
    changes: Array<[number, number, any, any]>,
    data: Array<Array<CellData>>,
    source: string,
    sourceCellDataArray: Array<Array<CellData>>,
) => {
    const minChangeRow = Math.min(...changes.map(([row]) => row));
    const minChangeCol = Math.min(...changes.map(([_, col]) => col));

    return changes.map((change) => {
        const [row, col, , newValue] = change;

        const prevCellData = propIn([row, col], data);

        if (sourceCellDataArray) {
            const nRowsOfSource = getRowCount(sourceCellDataArray);
            const nColsOfSource = getColumnCount(sourceCellDataArray);
            const sourceRow = (row - minChangeRow) % nRowsOfSource;
            const sourceCol = (col - minChangeCol) % nColsOfSource;

            const sourceData = cloneDeep(sourceCellDataArray[sourceRow][sourceCol]);

            if (source === 'Autofill.fill' && isFormula(sourceData.value)) {
                sourceData.value = newValue;
            }

            return [row, col, prevCellData, sourceData];
        } else {
            return [row, col, prevCellData, createTableCellEntry(newValue)];
        }
    });
};
